import {
  Bot,
  BotMessageSquare,
  ChevronDownIcon,
  type LucideIcon,
  MessageCircleMore,
  MessageSquareMore,
  X,
} from 'lucide-react';

import { type FC, forwardRef, useEffect, useState } from 'react';
import { AssistantModalPrimitive } from '@assistant-ui/react';

import { Thread } from './thread';
import { AttentionBubble } from './AttentionBubble';
import { Button } from '@/components/ui/button';
import { useChatbotConfig } from '../hooks/useChatbotConfig';
import { useMobileChatbot } from '../hooks/useMobileChatbot';
import { ChatbotIcon, ChatbotPosition } from '../types/customTypes';
import { SessionManager } from '../utils/sessionManager';

const MODAL_DIMENSIONS = {
  height: 638,
  width: 425,
} as const;

export const AssistantModal: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { config, color, colorOverTheme } = useChatbotConfig();
  const { mobileStyles, setViewportRef } = useMobileChatbot(isModalOpen);

  // Initialize modal state from session storage on mount
  useEffect(() => {
    if (SessionManager.hasValidSession()) {
      const savedState = SessionManager.loadModalState();
      setIsModalOpen(savedState);
    }
  }, []);

  // Add data-modern-ui-react to portaled modal content
  useEffect(() => {
    if (isModalOpen) {
      // Find the portal wrapper and add the scoping attribute
      setTimeout(() => {
        const portalWrapper = document.querySelector(
          '[data-radix-popper-content-wrapper]',
        );
        if (portalWrapper) {
          portalWrapper.setAttribute('data-modern-ui-react', '');
        }
      }, 0);
    }
  }, [isModalOpen]);

  // Handle toggle button (FAB) - preserves session, only changes visibility
  const handleToggle = (open: boolean) => {
    setIsModalOpen(open);
    SessionManager.saveModalState(open);

    if (open) {
      // Dismiss attention bubble when modal opens
      SessionManager.dismissBubble();
      // Notify AttentionBubble component to hide immediately
      window.dispatchEvent(new CustomEvent('chatbot-bubble-dismissed'));
    } else {
      // Save messages when hiding modal
      window.dispatchEvent(new CustomEvent('chatbot-modal-closing'));
    }
  };

  const handleAttentionBubbleClick = () => {
    handleToggle(true);
  };

  // Handle close button (X) - closes modal and preserves session
  const handleClose = () => {
    // Save messages when closing modal
    window.dispatchEvent(new CustomEvent('chatbot-modal-closing'));

    // Close the modal (session persists for conversation continuity)
    setIsModalOpen(false);
    SessionManager.saveModalState(false);
  };

  // Get position classes based on configuration
  const getPositionClasses = () => {
    if (config?.position === ChatbotPosition.BottomLeft) {
      return 'fixed bottom-[72px] md:bottom-4 left-4 size-16';
    }
    return 'fixed bottom-[72px] md:bottom-4 right-4 size-16';
  };

  // Get modal animation classes based on position
  const getModalAnimationClasses = () => {
    const baseClasses = `bg-popover text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out data-[state=open]:zoom-in data-[state=open]:slide-in-from-bottom-1/2 data-[state=closed]:slide-out-to-bottom-1/2 h-[${MODAL_DIMENSIONS.height}px] w-[${MODAL_DIMENSIONS.width}px] overflow-clip rounded-xl p-0 shadow-2xl outline-none [&>.thread-root]:bg-inherit flex flex-col`;

    if (config?.position === ChatbotPosition.BottomLeft) {
      return `${baseClasses} data-[state=open]:slide-in-from-left-1/2 data-[state=closed]:slide-out-to-left-1/2`;
    }

    return `${baseClasses} data-[state=open]:slide-in-from-right-1/2 data-[state=closed]:slide-out-to-right-1/2`;
  };

  // Get the appropriate icon component based on configuration
  const getChatbotIcon = () => {
    const iconType = config?.icon;
    const iconUrl = config?.iconUrl;

    switch (iconType) {
      case ChatbotIcon.Chatbot1:
        return Bot;
      case ChatbotIcon.Chatbot2:
        return MessageSquareMore;
      case ChatbotIcon.Chatbot3:
        return BotMessageSquare;
      case ChatbotIcon.Chatbot4:
        return MessageCircleMore;
      case ChatbotIcon.UserDefined:
        return iconUrl ? 'custom' : Bot;
      default:
        return Bot;
    }
  };

  // Header chatbot icon (mirrors the launcher icon), rendered left of the name
  const HeaderIcon = getChatbotIcon();
  const HeaderIconComponent = HeaderIcon === 'custom' ? Bot : HeaderIcon;
  const headerHasCustomImage = HeaderIcon === 'custom' && !!config?.iconUrl;

  return (
    <>
      {/* Mobile responsive styles - static 100vh/100vw fullscreen */}
      <style>{mobileStyles}</style>
      <AssistantModalPrimitive.Root
        open={isModalOpen}
        onOpenChange={handleToggle}
      >
        <AssistantModalPrimitive.Anchor className={getPositionClasses()}>
          <AttentionBubble
            bubbleText={config?.bubbleText || ''}
            position={
              config?.position === ChatbotPosition.BottomLeft ? 'left' : 'right'
            }
            animationEnabled={config?.animationEnabled ?? true}
            onContentClick={handleAttentionBubbleClick}
          >
            <AssistantModalPrimitive.Trigger asChild>
              <AssistantModalButton
                iconComponent={getChatbotIcon()}
                iconUrl={config?.iconUrl || null}
                buttonColor={color}
                iconColor={colorOverTheme}
              />
            </AssistantModalPrimitive.Trigger>
          </AttentionBubble>
        </AssistantModalPrimitive.Anchor>
        <AssistantModalPrimitive.Content
          sideOffset={16}
          className={getModalAnimationClasses()}
          align={
            config?.position === ChatbotPosition.BottomLeft ? 'start' : 'end'
          }
          style={{ zIndex: 1000 }}
        >
          {/* Header */}
          <div
            className='flex items-center gap-3 w-full px-3 py-4 flex-none shadow-md z-1'
            style={{ backgroundColor: colorOverTheme }}
          >
            <div className='flex flex-1 items-center gap-2 min-w-0'>
              {headerHasCustomImage ? (
                <img
                  src={config?.iconUrl ?? undefined}
                  alt='Chatbot icon'
                  className='h-6 w-6 rounded-full object-cover'
                />
              ) : (
                <HeaderIconComponent
                  className='h-6 w-6 shrink-0'
                  style={{ color }}
                />
              )}
              <div className='text-base font-medium truncate' style={{ color }}>
                {config?.name || ''}
              </div>
            </div>
            <button
              type='button'
              onClick={handleClose}
              aria-label='Close Assistant'
              className='shrink-0 cursor-pointer opacity-70 transition-opacity hover:opacity-100 focus:outline-none'
            >
              <X className='size-5' style={{ color }} />
            </button>
          </div>

          {/* Content Area */}
          <div className='flex-1 overflow-hidden'>
            <Thread setViewportRef={setViewportRef} />
          </div>

          {/* Footer */}
          <div className='text-center py-2 px-4 flex-none relative bg-background text-[0px]'>
            <a
              href='https://www.edlio.com/'
              target='_blank'
              rel='noopener noreferrer'
              className='text-xs font-normal !text-neutral-500 hover:!text-neutral-700 transition-colors'
            >
              Powered by Edlio AI
            </a>
          </div>
        </AssistantModalPrimitive.Content>
      </AssistantModalPrimitive.Root>
    </>
  );
};

type AssistantModalButtonProps = {
  'data-state'?: 'open' | 'closed';
  iconComponent: LucideIcon | 'custom';
  iconUrl: string | null;
  buttonColor: string;
  iconColor: string;
};

const AssistantModalButton = forwardRef<
  HTMLButtonElement,
  AssistantModalButtonProps
>(
  (
    {
      'data-state': state,
      iconComponent,
      iconUrl,
      buttonColor,
      iconColor,
      ...rest
    },
    ref,
  ) => {
    const baseIconClasses = 'absolute size-10 transition-all';
    const mainIconAnimationClasses =
      'data-[state=closed]:rotate-0 data-[state=open]:rotate-90 data-[state=closed]:scale-100 data-[state=open]:scale-0';
    const chevronAnimationClasses =
      'data-[state=closed]:-rotate-90 data-[state=open]:rotate-0 data-[state=closed]:scale-0 data-[state=open]:scale-100';

    const renderIcon = () => {
      if (iconComponent === 'custom' && iconUrl) {
        return (
          <img
            src={iconUrl}
            alt='Chatbot Icon'
            data-state={state}
            className={`${baseIconClasses} ${mainIconAnimationClasses}`}
            style={{ color: iconColor }}
          />
        );
      } else if (iconComponent !== 'custom') {
        const IconComponent = iconComponent;
        return (
          <IconComponent
            data-state={state}
            className={`${baseIconClasses} ${mainIconAnimationClasses}`}
            style={{ color: iconColor }}
            strokeWidth={1.5}
          />
        );
      }
      // Fallback to default icon if custom but no iconUrl
      return (
        <Bot
          data-state={state}
          className={`${baseIconClasses} ${mainIconAnimationClasses}`}
          style={{ color: iconColor }}
          strokeWidth={1.5}
        />
      );
    };

    return (
      <Button
        variant='default'
        {...rest}
        className='size-full rounded-full shadow-md transition-transform hover:scale-110 active:scale-90'
        style={{ backgroundColor: buttonColor }}
        ref={ref}
      >
        {renderIcon()}

        <ChevronDownIcon
          data-state={state}
          className={`${baseIconClasses} ${chevronAnimationClasses}`}
          style={{ color: iconColor }}
          strokeWidth={1.5}
        />
        <span className='sr-only'>
          {state === 'open' ? 'Close Assistant' : 'Open Assistant'}
        </span>
      </Button>
    );
  },
);

AssistantModalButton.displayName = 'AssistantModalButton';
